import type { NavIcon } from '@quadralize/ui'
import { Calendar, Heart, HomeAngle, Magnifier, UserCircle } from '@solar-icons/react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'
import { useLogout } from '@/modules/auth/hooks/use-logout'
import { toAuthUserDisplay } from '@/modules/auth/utils/auth-user-display'
import { useAuthStore } from '@/store/use-auth-store'

interface NavItemConfig {
  labelKey: string
  path: string
  icon: NavIcon
  matchPaths: string[]
}

interface NavSectionConfig {
  labelKey: string
  items: NavItemConfig[]
}

/* Favoritas comes before Minha conta so `/conta/favoritas` resolves to it first */
const NAV_SECTIONS: NavSectionConfig[] = [
  {
    labelKey: 'nav.sectionPlay',
    items: [
      { labelKey: 'nav.discover', path: '/', icon: HomeAngle, matchPaths: ['/'] },
      {
        labelKey: 'nav.searchCourts',
        path: '/buscar',
        icon: Magnifier,
        matchPaths: ['/buscar', '/quadra', '/reserva/sucesso'],
      },
      {
        labelKey: 'nav.myReservations',
        path: '/reservas',
        icon: Calendar,
        matchPaths: ['/reservas'],
      },
      {
        labelKey: 'nav.favorites',
        path: '/conta/favoritas',
        icon: Heart,
        matchPaths: ['/conta/favoritas'],
      },
    ],
  },
  {
    labelKey: 'nav.sectionAccount',
    items: [
      { labelKey: 'nav.myAccount', path: '/conta', icon: UserCircle, matchPaths: ['/conta'] },
    ],
  },
]

export interface AppSidebarNavItem {
  label: string
  path: string
  icon: NavIcon
  active: boolean
}

export interface AppSidebarNavSection {
  label: string
  items: AppSidebarNavItem[]
}

interface UseAppSidebarResult {
  sections: AppSidebarNavSection[]
  loggedIn: boolean
  userName: string
  userInitials: string
  handleNavigate: (path: string) => void
  handleSignOut: () => void
}

export function useAppSidebar(): UseAppSidebarResult {
  const { t } = useTranslation('play')
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const loggedIn = useAuthStore((state) => state.status === 'authenticated')
  const authUser = useAuthStore((state) => state.user)
  const user = toAuthUserDisplay(authUser)
  const logout = useLogout()

  const activePath = useMemo(() => {
    const allItems = NAV_SECTIONS.flatMap((section) => section.items)
    const matchedItem = allItems.find((item) =>
      item.path === '/'
        ? pathname === '/'
        : item.matchPaths.some((matchPath) => pathname.startsWith(matchPath)),
    )

    return matchedItem?.path ?? pathname
  }, [pathname])

  const sections = useMemo<AppSidebarNavSection[]>(
    () =>
      NAV_SECTIONS.map((section) => ({
        label: t(section.labelKey),
        items: section.items.map((item) => ({
          label: t(item.labelKey),
          path: item.path,
          icon: item.icon,
          active: item.path === activePath,
        })),
      })),
    [t, activePath],
  )

  function handleNavigate(path: string): void {
    navigate(path)
  }

  function handleSignOut(): void {
    logout()
  }

  return {
    sections,
    loggedIn,
    userName: user.name,
    userInitials: user.initials,
    handleNavigate,
    handleSignOut,
  }
}
